<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { splitName } from '@chatwoot/utils';
import countries from 'shared/constants/countries.js';
import { useLocalizedLists } from 'shared/composables/useLocalizedLists';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';

const props = defineProps({
  contactData: {
    type: Object,
    default: null,
  },
  isDetailsView: {
    type: Boolean,
    default: false,
  },
  isNewContact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const FORM_CONFIG = {
  FIRST_NAME: { field: 'firstName' },
  LAST_NAME: { field: 'lastName' },
  EMAIL_ADDRESS: { field: 'email' },
  PROFILE_EMAIL: { field: 'profileEmail' },
  PHONE_NUMBER: { field: 'phoneNumber' },
  CITY: { field: 'additionalAttributes.city' },
  COUNTRY: { field: 'additionalAttributes.countryCode' },
  BIO: { field: 'additionalAttributes.description' },
  COMPANY_NAME: { field: 'additionalAttributes.companyName' },
};

const SOCIAL_CONFIG = {
  LINKEDIN: 'i-ri-linkedin-box-fill',
  FACEBOOK: 'i-ri-facebook-circle-fill',
  INSTAGRAM: 'i-ri-instagram-line',
  TELEGRAM: 'i-ri-telegram-fill',
  TIKTOK: 'i-ri-tiktok-fill',
  TWITTER: 'i-ri-twitter-x-fill',
  GITHUB: 'i-ri-github-fill',
};

const defaultState = {
  id: 0,
  name: '',
  email: '',
  profileEmail: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  additionalAttributes: {
    description: '',
    companyName: '',
    countryCode: '',
    country: '',
    city: '',
    socialProfiles: {
      facebook: '',
      github: '',
      instagram: '',
      telegram: '',
      tiktok: '',
      linkedin: '',
      twitter: '',
    },
  },
};

const state = reactive({ ...defaultState });

// Nome vazio é permitido de propósito: apagá-lo é como o atendente pede que o
// contato volte a exibir o nome vindo do WhatsApp. O Aceleres ouve o webhook
// `contact_updated`, resolve o nome pela hierarquia (agenda do WhatsApp →
// pushName) e o devolve pela API.
//
// Sem esse retorno o contato ficaria anônimo, porque o Chatwoot guarda um
// `name` só e não tem memória do nome original do WhatsApp.
const validationRules = {
  email: { email },
  profileEmail: { email },
};

const v$ = useVuelidate(validationRules, state);
const emailServerError = ref('');
const isPhoneInvalid = ref(false);

const isFormInvalid = computed(
  () => v$.value.$invalid || (props.isDetailsView && isPhoneInvalid.value)
);

const prepareStateBasedOnProps = () => {
  if (props.isNewContact) {
    return; // Added to prevent state update for new contact form
  }

  emailServerError.value = '';

  const {
    id,
    name = '',
    email: emailAddress,
    profileEmail = '',
    phoneNumber,
    additionalAttributes = {},
  } = props.contactData || {};
  const { firstName, lastName } = splitName(name || '');
  const {
    description = '',
    companyName = '',
    countryCode = '',
    country = '',
    city = '',
    socialTelegramUserName = '',
    socialProfiles = {},
  } = additionalAttributes || {};

  const telegramUsername =
    socialProfiles?.telegram || socialTelegramUserName || '';

  Object.assign(state, {
    id,
    name,
    firstName,
    lastName,
    email: emailAddress,
    profileEmail,
    phoneNumber,
    additionalAttributes: {
      description,
      companyName,
      countryCode,
      country,
      city,
      socialProfiles: {
        ...socialProfiles,
        telegram: telegramUsername,
      },
    },
  });
};

// O `id` continua sendo o valor gravado; só o rótulo é traduzido.
const { localizedCountries } = useLocalizedLists();
const countryOptions = computed(() =>
  localizedCountries.value.map(({ name, id }) => ({ label: name, value: id }))
);

const getFieldLabel = key => {
  if (key === 'EMAIL_ADDRESS') {
    return t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.EMAIL_ADDRESS.LABEL');
  }
  if (key === 'PROFILE_EMAIL') {
    return t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.PROFILE_EMAIL.LABEL');
  }
  return '';
};

const editDetailsForm = computed(() =>
  Object.keys(FORM_CONFIG)
    .filter(key => key !== 'PROFILE_EMAIL' || props.isDetailsView)
    .map(key => ({
      key,
      placeholder: t(
        `CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.${key}.PLACEHOLDER`
      ),
      label: getFieldLabel(key),
    }))
);

const expandedSocials = ref([]);
const detailGroups = computed(() => {
  if (!props.isDetailsView)
    return [{ key: 'default', fields: editDetailsForm.value }];
  return [
    { key: 'IDENTIFICATION', keys: ['FIRST_NAME', 'LAST_NAME'] },
    { key: 'SERVICE', keys: [] },
    {
      key: 'CONTACT',
      keys: ['PHONE_NUMBER', 'EMAIL_ADDRESS', 'PROFILE_EMAIL'],
    },
    { key: 'COMPANY', keys: ['COMPANY_NAME', 'CITY', 'COUNTRY', 'BIO'] },
  ].map(group => ({
    ...group,
    fields: group.keys.map(key =>
      editDetailsForm.value.find(field => field.key === key)
    ),
  }));
});

const socialProfilesForm = computed(() =>
  Object.entries(SOCIAL_CONFIG).map(([key, icon]) => ({
    key,
    placeholder: t(`CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.FORM.${key}.PLACEHOLDER`),
    icon,
  }))
);

const isValidationField = key => {
  const field = FORM_CONFIG[key]?.field;
  return ['firstName', 'email', 'profileEmail'].includes(field);
};

const getValidationKey = key => {
  return FORM_CONFIG[key]?.field;
};

// Creates a computed property for two-way form field binding
const getFormBinding = key => {
  const field = FORM_CONFIG[key]?.field;
  if (!field) return null;

  return computed({
    get: () => {
      // Handle firstName/lastName fields
      if (field === 'firstName' || field === 'lastName') {
        return state[field]?.toString() || '';
      }

      // Handle nested vs non-nested fields
      const [base, nested] = field.split('.');
      // Example: 'email' → state.email
      // Example: 'additionalAttributes.city' → state.additionalAttributes.city
      return (nested ? state[base][nested] : state[base])?.toString() || '';
    },

    set: async value => {
      // Handle name fields specially to maintain the combined 'name' field
      if (field === 'firstName' || field === 'lastName') {
        state[field] = value;
        // Example: firstName="John", lastName="Doe" → name="John Doe"
        state.name = `${state.firstName} ${state.lastName}`.trim();
      } else {
        // Handle nested vs non-nested fields
        const [base, nested] = field.split('.');
        if (nested) {
          // Example: additionalAttributes.city = "New York"
          state[base][nested] = value;
        } else {
          // Example: email = "test@example.com"
          state[base] = value;
        }
      }

      if (field === 'email') emailServerError.value = '';

      const isFormValid = await v$.value.$validate();
      if (isFormValid) {
        const { firstName, lastName, ...stateWithoutNames } = state;
        const payload = { ...stateWithoutNames };
        if (!props.isDetailsView) delete payload.profileEmail;
        emit('update', payload);
      }
    },
  });
};

const getMessageType = key => {
  if (key === 'EMAIL_ADDRESS' && emailServerError.value) return 'error';
  return isValidationField(key) && v$.value[getValidationKey(key)]?.$error
    ? 'error'
    : 'info';
};

const getMessage = key => {
  if (key === 'EMAIL_ADDRESS' && emailServerError.value) {
    return emailServerError.value;
  }
  if (!isValidationField(key) || !v$.value[getValidationKey(key)]?.$error) {
    return key === 'PROFILE_EMAIL'
      ? t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.PROFILE_EMAIL.HELP')
      : '';
  }
  return key === 'PROFILE_EMAIL'
    ? t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.PROFILE_EMAIL.ERROR')
    : t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.EMAIL_ADDRESS.ERROR');
};

const setEmailError = message => {
  emailServerError.value = message;
};

const clearEmailError = () => {
  emailServerError.value = '';
};

const handleCountrySelection = value => {
  const selectedCountry = countries.find(option => option.id === value);
  state.additionalAttributes.country = selectedCountry?.name || '';
  const { firstName, lastName, ...payload } = state;
  if (!props.isDetailsView) delete payload.profileEmail;
  emit('update', payload);
};

const normalizeField = key => {
  if (!props.isDetailsView) return;
  const binding = getFormBinding(key);
  const value = binding.value.trim();
  if (['EMAIL_ADDRESS', 'PROFILE_EMAIL'].includes(key)) {
    binding.value = value.toLowerCase();
  } else if (
    ['FIRST_NAME', 'LAST_NAME', 'CITY', 'COMPANY_NAME'].includes(key)
  ) {
    binding.value = value.replace(/\s+/g, ' ');
  } else {
    binding.value = value;
  }
};

const updateSocialProfile = (key, value) => {
  state.additionalAttributes.socialProfiles[key.toLowerCase()] = value
    .trim()
    .replace(/^@/, '')
    .replace(/\s/g, '');
  const { firstName, lastName, ...payload } = state;
  if (!props.isDetailsView) delete payload.profileEmail;
  emit('update', payload);
};

const resetValidation = () => {
  v$.value.$reset();
};

const resetForm = () => {
  Object.assign(state, defaultState);
};

watch(
  () => props.contactData?.id,
  id => {
    if (id) prepareStateBasedOnProps();
  },
  { immediate: true }
);

// Expose state to parent component for avatar upload
defineExpose({
  state,
  resetValidation,
  isFormInvalid,
  resetForm,
  setEmailError,
  clearEmailError,
});
</script>

<template>
  <div
    class="w-full gap-3"
    :class="[
      isDetailsView
        ? 'grid grid-cols-1 min-[600px]:grid-cols-2 items-start'
        : 'flex flex-col',
      { 'max-md:gap-4': isNewContact },
    ]"
  >
    <div
      v-for="group in detailGroups"
      :key="group.key"
      class="flex min-w-0 flex-col items-start gap-2"
      :class="{
        'max-md:gap-3': isNewContact,
        'rounded-2xl bg-white p-4 shadow-[0_8px_24px_rgba(0,0,0,0.04)]':
          isDetailsView,
      }"
    >
      <span
        class="py-1 text-sm font-medium text-n-slate-12"
        :class="{ 'max-md:hidden': isNewContact }"
      >
        {{
          isDetailsView
            ? t(`CONTACT_DETAIL_VIEW.${group.key}`)
            : t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.TITLE')
        }}
      </span>
      <p v-if="isDetailsView" class="-mt-2 mb-1 text-xs text-n-slate-10">
        {{ t(`CONTACT_DETAIL_VIEW.${group.key}_HELP`) }}
      </p>
      <div
        class="grid w-full grid-cols-1 gap-3 min-[600px]:grid-cols-2"
        :class="{ 'max-md:gap-3': isNewContact }"
      >
        <div v-for="item in group.fields" :key="item.key" class="min-w-0">
          <span
            v-if="isDetailsView && !item.label"
            class="mb-1 block text-xs font-medium text-n-slate-11"
            >{{ t(`CONTACT_DETAIL_VIEW.FIELDS.${item.key}`) }}</span
          >
          <ComboBox
            v-if="item.key === 'COUNTRY'"
            v-model="state.additionalAttributes.countryCode"
            :options="countryOptions"
            :placeholder="item.placeholder"
            class="[&>div>button]:h-8 max-md:[&>div>button>span:first-child]:!size-[22px]"
            :class="{
              '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
                !isDetailsView,
              '[&>div>button]:!h-10 [&>div>button]:!bg-white [&>div>button]:!rounded-lg [&>div>button]:!text-sm':
                isDetailsView,
            }"
            @update:model-value="handleCountrySelection"
          />
          <PhoneNumberInput
            v-else-if="item.key === 'PHONE_NUMBER'"
            v-model="getFormBinding(item.key).value"
            :placeholder="item.placeholder"
            :show-border="isDetailsView"
            :format-national="isDetailsView"
            :class="{
              '[&>div:first-child]:!h-10 [&>div:first-child]:!bg-white [&_input]:!h-10':
                isDetailsView,
            }"
            @validation-change="isPhoneInvalid = $event"
          />
          <Input
            v-else
            v-model="getFormBinding(item.key).value"
            :placeholder="item.placeholder"
            :label="item.label"
            :type="
              ['EMAIL_ADDRESS', 'PROFILE_EMAIL'].includes(item.key)
                ? 'email'
                : 'text'
            "
            :maxlength="
              ['EMAIL_ADDRESS', 'PROFILE_EMAIL'].includes(item.key)
                ? 100
                : undefined
            "
            :message="getMessage(item.key)"
            :message-type="getMessageType(item.key)"
            :custom-input-class="`h-8 !pt-1 !pb-1 ${
              !isDetailsView
                ? '[&:not(.error,.focus)]:!outline-transparent'
                : '!h-10 !bg-white text-sm'
            }`"
            class="w-full"
            :class="{
              '[&>label]:!text-xs [&>label]:!font-medium [&>label]:!text-n-slate-11':
                isDetailsView,
            }"
            @input="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
            @blur="
              normalizeField(item.key);
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch();
            "
          />
        </div>
        <slot
          v-if="!isDetailsView || group.key === 'SERVICE'"
          name="details-attributes"
        />
      </div>
    </div>
    <div
      class="flex flex-col items-start gap-2"
      :class="{
        'min-[600px]:col-span-2 rounded-2xl bg-white p-4 shadow-[0_8px_24px_rgba(0,0,0,0.04)]':
          isDetailsView,
        'max-md:gap-3 max-md:border-t max-md:border-n-weak max-md:pt-4':
          isNewContact,
      }"
    >
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{
          isDetailsView
            ? t('CONTACT_DETAIL_VIEW.SOCIAL')
            : t('CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.TITLE')
        }}
      </span>
      <p v-if="isDetailsView" class="-mt-2 mb-1 text-xs text-n-slate-10">
        {{ t('CONTACT_DETAIL_VIEW.SOCIAL_HELP') }}
      </p>
      <div
        v-if="isDetailsView"
        class="grid w-full grid-cols-1 gap-3 min-[600px]:grid-cols-2"
      >
        <div v-for="item in socialProfilesForm" :key="item.key" class="min-w-0">
          <label
            :for="`contact-social-${item.key}`"
            class="mb-1 flex items-center gap-1.5 text-xs font-medium text-n-slate-11"
          >
            <Icon :icon="item.icon" class="size-4" />
            {{
              item.key === 'GITHUB'
                ? 'GitHub'
                : item.key === 'LINKEDIN'
                  ? 'LinkedIn'
                  : item.key === 'TIKTOK'
                    ? 'TikTok'
                    : item.key.charAt(0) + item.key.slice(1).toLowerCase()
            }}
          </label>
          <Input
            :id="`contact-social-${item.key}`"
            :model-value="
              state.additionalAttributes.socialProfiles[
                item.key.toLowerCase()
              ] || ''
            "
            :placeholder="item.placeholder"
            custom-input-class="!h-10 !bg-white"
            @update:model-value="updateSocialProfile(item.key, $event)"
          />
        </div>
      </div>
      <div
        v-else
        class="flex flex-wrap gap-2"
        :class="{
          'max-md:grid max-md:w-full max-md:grid-cols-1 max-md:gap-2.5':
            isNewContact,
        }"
      >
        <div
          v-for="item in socialProfilesForm"
          :key="item.key"
          class="flex items-center h-8 gap-2 px-2 rounded-lg"
          :class="{
            '!h-auto !px-0': isDetailsView,
            'bg-n-alpha-2 dark:bg-n-solid-3': !isDetailsView,
            'max-md:h-10 max-md:w-full max-md:border max-md:border-n-weak max-md:px-3':
              isNewContact,
          }"
        >
          <button
            v-if="
              isDetailsView &&
              !expandedSocials.includes(item.key) &&
              !state.additionalAttributes.socialProfiles[item.key.toLowerCase()]
            "
            type="button"
            class="h-8 !rounded-lg !border !border-solid !border-n-weak !bg-white px-2 text-xs text-n-slate-11 hover:bg-n-alpha-1"
            @click="expandedSocials.push(item.key)"
          >
            {{
              `+ ${item.key === 'GITHUB' ? 'Github' : item.key === 'LINKEDIN' ? 'LinkedIn' : item.key === 'TIKTOK' ? 'TikTok' : item.key.charAt(0) + item.key.slice(1).toLowerCase()}`
            }}
          </button>
          <Icon
            v-if="
              !isDetailsView ||
              expandedSocials.includes(item.key) ||
              state.additionalAttributes.socialProfiles[item.key.toLowerCase()]
            "
            :icon="item.icon"
            class="flex-shrink-0 text-n-slate-11 size-4"
          />
          <input
            v-if="
              !isDetailsView ||
              expandedSocials.includes(item.key) ||
              state.additionalAttributes.socialProfiles[item.key.toLowerCase()]
            "
            v-model="
              state.additionalAttributes.socialProfiles[item.key.toLowerCase()]
            "
            class="w-auto min-w-[100px] text-sm bg-transparent outline-none reset-base text-n-slate-12 dark:text-n-slate-12 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10"
            :class="{ 'max-md:min-w-0 max-md:flex-1': isNewContact }"
            :placeholder="item.placeholder"
            :size="item.placeholder.length"
            @input="emit('update', state)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
